import { useState, useRef } from "react";
import { Upload, FileText, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadPage = ({ onUploadSuccess }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error("Please upload a CSV file only");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload-manifest`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`Successfully processed ${response.data.processed_items} items!`);
      onUploadSuccess(response.data);
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to process the manifest file';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <h1 
          className="text-4xl sm:text-5xl font-black tracking-tight heading-font" 
          data-testid="page-title"
        >
          Transform Your Manifest
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto body-font" data-testid="page-subtitle">
          Upload your inventory manifest and automatically generate a Shopify-ready CSV with calculated pricing
        </p>
      </div>

      {/* Upload Section */}
      <div className="max-w-4xl mx-auto">
        <div 
          className={`upload-dropzone relative p-12 text-center ${isDragging ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid="upload-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          
          {isUploading ? (
            <div className="space-y-4" data-testid="uploading-state">
              <div className="w-16 h-16 mx-auto border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
              <p className="text-lg font-medium text-slate-700 mono-font">Processing Manifest...</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="w-16 h-16 mx-auto p-4 bg-slate-100 rounded-sm" data-testid="upload-icon">
                <Upload className="w-full h-full text-slate-600" />
              </div>
              
              <div className="space-y-2">
                <h3 className="text-xl font-bold heading-font" data-testid="upload-title">
                  Drop your manifest CSV here
                </h3>
                <p className="text-slate-600 mono-font text-sm" data-testid="upload-instructions">
                  or click to select file • Supports: .CSV format only
                </p>
              </div>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="refinery-button"
                data-testid="select-file-button"
              >
                <FileText className="w-4 h-4 mr-2 inline" />
                Select File
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Rules Info */}
      <div className="max-w-4xl mx-auto bg-slate-50 rounded-sm p-6 space-y-4 relative noise-overlay">
        <div className="flex items-center space-x-2 mb-4">
          <AlertCircle className="w-5 h-5 text-slate-600" />
          <h4 className="font-bold heading-font" data-testid="pricing-rules-title">Pricing Calculation Rules</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mono-font text-sm">
          <div className="space-y-2" data-testid="general-pricing-rule">
            <div className="category-badge category-badge-general">General Merchandise</div>
            <p>Sell: <span className="font-bold text-emerald-700">65%</span> • Cost: <span className="font-bold text-slate-600">28%</span></p>
            <p>Profit: <span className="font-bold text-emerald-700">37%</span> of retail</p>
          </div>
          
          <div className="space-y-2" data-testid="baby-care-pricing-rule">
            <div className="category-badge category-badge-baby">Baby Care</div>
            <p>Sell: <span className="font-bold text-emerald-700">70%</span> • Cost: <span className="font-bold text-slate-600">28%</span></p>
            <p>Profit: <span className="font-bold text-emerald-700">42%</span> of retail</p>
          </div>
          
          <div className="space-y-2" data-testid="apparel-pricing-rule">
            <div className="category-badge category-badge-apparel">Apparel & Accessories</div>
            <p>Sell: <span className="font-bold text-emerald-700">50%</span> • Cost: <span className="font-bold text-slate-600">18%</span></p>
            <p>Profit: <span className="font-bold text-emerald-700">32%</span> of retail</p>
          </div>
          
          <div className="space-y-2" data-testid="food-pricing-rule">
            <div className="category-badge category-badge-general">Food & Paper Goods</div>
            <p>Sell: <span className="font-bold text-emerald-700">60%</span> • Cost: <span className="font-bold text-slate-600">18%</span></p>
            <p>Profit: <span className="font-bold text-emerald-700">42%</span> of retail</p>
          </div>
        </div>
      </div>

      {/* Expected Format */}
      <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-sm p-6">
        <h4 className="font-bold heading-font mb-4" data-testid="expected-format-title">Expected CSV Format</h4>
        <div className="overflow-x-auto">
          <table className="data-table w-full text-xs" data-testid="format-example-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>ItemNumber</th>
                <th>UPC Number</th>
                <th>Category</th>
                <th>Category description</th>
                <th>Qty</th>
                <th>Retail per item</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>MONSTER GI30</td>
                <td>990361625</td>
                <td>0082739655071</td>
                <td>61</td>
                <td>BATTERIES AND EMERGING TECH</td>
                <td>1.0</td>
                <td>$494.00</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UploadPage;